import { ErrorCode, type ApiErrorBody } from '@brimo/shared';

/** Error thrown for every non-2xx API response; carries the server's code, message and details. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  /** `details.fields` of a VALIDATION_FAILED response, or an empty object. */
  get fields(): Record<string, string> {
    const fields = this.details?.['fields'];
    if (typeof fields !== 'object' || fields === null) return {};
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) if (typeof value === 'string') result[key] = value;
    return result;
  }

  /** A numeric detail (`remainingAttempts`, `attemptsUsed`, …) or null. */
  detailNumber(key: string): number | null {
    const value = this.details?.[key];
    return typeof value === 'number' ? value : null;
  }

  detailString(key: string): string | null {
    const value = this.details?.[key];
    return typeof value === 'string' ? value : null;
  }
}

export const GENERIC_ERROR_MESSAGE = 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';

/** Message of an unknown error for a banner: the API's German message, or the generic one. */
export function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : GENERIC_ERROR_MESSAGE;
}

/** Fired on `window` when a workspace request answers 401 (session expired or ended elsewhere). */
export const UNAUTHENTICATED_EVENT = 'brimo:unauthenticated';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/** Same-origin JSON client. Cookies carry the session; Origin/Sec-Fetch-Site protect against CSRF. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    signal: options.signal ?? null,
  };
  if (options.body !== undefined) {
    init.headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
    init.body = JSON.stringify(options.body);
  }
  const response = await fetch(`/api${path}`, init);
  const text = await response.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const body = (json as Partial<ApiErrorBody> | null)?.error ?? {
      code: ErrorCode.INTERNAL,
      message: GENERIC_ERROR_MESSAGE,
    };
    // The auth routes answer 401 as part of their contract (wrong password, 2FA pending); everywhere
    // else a 401 means the session is gone and the app has to return to the login page.
    if (response.status === 401 && body.code === ErrorCode.UNAUTHENTICATED && !path.startsWith('/auth/')) {
      window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    }
    throw new ApiError(response.status, body);
  }
  return json as T;
}

function throwFor(response: Response, json: unknown, path: string): never {
  const body = (json as Partial<ApiErrorBody> | null)?.error ?? {
    code: ErrorCode.INTERNAL,
    message: GENERIC_ERROR_MESSAGE,
  };
  if (response.status === 401 && body.code === ErrorCode.UNAUTHENTICATED && !path.startsWith('/auth/')) {
    window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
  }
  throw new ApiError(response.status, body);
}

/** A file name from a `Content-Disposition` header (`filename="…"` or `filename*=UTF-8''…`), or the fallback. */
function fileNameOf(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return fallback;
    }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? fallback;
}

/**
 * `POST` that answers with a file (`POST /contacts/export` → CSV): the response is saved through a
 * temporary object URL under the server's file name. Errors arrive as the usual JSON envelope.
 */
export async function apiDownload(path: string, body: unknown, fallbackName = 'export.csv'): Promise<string> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { Accept: 'text/csv, application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text();
    throwFor(response, text ? (JSON.parse(text) as unknown) : null, path);
  }
  const blob = await response.blob();
  const name = fileNameOf(response.headers.get('Content-Disposition'), fallbackName);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/** Multipart `POST` (`POST /documents` with a file part); the browser sets the boundary itself. */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    body: formData,
  });
  const text = await response.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) throwFor(response, json, path);
  return json as T;
}
